/**
 * FamilyModules - Module & Add-ons Management (PER SENIOR)
 * 
 * Features:
 * - View all available skills
 * - Enable/Disable skills per SENIOR (not household)
 * - Grouped by pricing tier (Core, Free, Plus, Premium)
 * - Core skills are read-only (cannot be disabled)
 * 
 * NEW: Per-Senior Module Management
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowLeft, Check, X, Lock, Key, User } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useFamilyAuth } from '../../contexts/FamilyAuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function FamilyModules() {
  const { household } = useFamilyAuth();
  const [loading, setLoading] = useState(true);
  const [seniors, setSeniors] = useState([]);
  const [selectedSeniorId, setSelectedSeniorId] = useState(null);
  const [seniorSkills, setSeniorSkills] = useState([]);
  const [toggling, setToggling] = useState({});

  // Load seniors on mount
  useEffect(() => {
    if (household?.id) {
      loadSeniors();
    }
  }, [household]);

  // Load skills when senior is selected
  useEffect(() => {
    if (selectedSeniorId) {
      loadModulesForSenior(selectedSeniorId);
    }
  }, [selectedSeniorId]);

  const loadSeniors = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/api/households/${household.id}/seniors`,
        { withCredentials: true }
      );
      setSeniors(response.data || []);
      
      // Auto-select first senior
      if (response.data && response.data.length > 0) {
        setSelectedSeniorId(response.data[0].id);
      } else {
        // No seniors found - stop loading
        setLoading(false);
        toast.error('Keine Senioren gefunden. Bitte fügen Sie zuerst einen Senior hinzu.');
      }
    } catch (error) {
      console.error('[Modules] Error loading seniors:', error);
      toast.error('Fehler beim Laden der Senioren');
      setLoading(false);
    }
  };

  const loadModulesForSenior = async (seniorId) => {
    try {
      setLoading(true);
      
      // Load skills for this senior
      const response = await axios.get(
        `${API}/api/seniors/${seniorId}/skills`,
        { timeout: 10000, withCredentials: true }
      );

      setSeniorSkills(response.data.skills || []);
      console.log('[Modules] Loaded skills for senior:', response.data.skills?.length);
    } catch (error) {
      console.error('[Modules] Load error:', error);
      toast.error('Fehler beim Laden der Module');
    } finally {
      setLoading(false);
    }
  };

  const toggleSkill = async (skill, currentlyEnabled) => {
    if (!selectedSeniorId) return;

    const skillKey = skill.skill.key || skill.skill.id;
    const skillName = skill.skill.name;

    // Prevent toggling core skills
    if (skill.skill.type === 'core') {
      toast.error('Kern-Module können nicht deaktiviert werden');
      return;
    }

    setToggling(prev => ({ ...prev, [skillKey]: true }));

    try {
      const action = currentlyEnabled ? 'disable' : 'enable';
      const endpoint = `${API}/api/seniors/${selectedSeniorId}/skills/${skillKey}/${action}`;

      await axios.post(endpoint, {}, { timeout: 5000, withCredentials: true });

      // Update local state
      setSeniorSkills(prev =>
        prev.map(s => {
          const sKey = s.skill?.key || s.skill?.id;
          return sKey === skillKey
            ? { ...s, enabled: !currentlyEnabled }
            : s;
        })
      );

      toast.success(
        currentlyEnabled
          ? `${skillName} deaktiviert`
          : `${skillName} aktiviert`
      );
    } catch (error) {
      console.error('[Modules] Toggle error:', error);
      const detail = error.response?.data?.detail || error.message;
      toast.error(detail || 'Fehler beim Ändern des Moduls');
    } finally {
      setToggling(prev => ({ ...prev, [skillKey]: false }));
    }
  };

  // Group skills by pricing tier
  const groupedSkills = {
    core: seniorSkills.filter(s => s.skill.type === 'core'),
    free: seniorSkills.filter(s => s.skill.type === 'free'),
    plus: seniorSkills.filter(s => s.skill.type === 'plus'),
    premium: seniorSkills.filter(s => s.skill.type === 'premium')
  };

  const renderSkillCard = (skillEntry) => {
    const skill = skillEntry.skill;
    const enabled = skillEntry.enabled;
    const isCore = skill.type === 'core';
    const skillKey = skill.key || skill.id;
    const isToggling = toggling[skillKey];

    return (
      <div
        key={skill.id}
        className={`bg-white rounded-xl shadow-md p-6 border-2 transition-all ${
          enabled
            ? 'border-green-400'
            : 'border-gray-200'
        }`}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{skill.icon}</span>
            <div>
              <h3 className="text-lg font-bold text-gray-900">{skill.name}</h3>
              <p className="text-sm text-gray-600">{skill.description}</p>
            </div>
          </div>

          {/* Toggle */}
          {isCore ? (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-full">
              <Lock className="w-4 h-4 text-gray-600" />
              <span className="text-xs font-medium text-gray-600">Kern-Modul</span>
            </div>
          ) : (
            <button
              onClick={() => toggleSkill(skillEntry, enabled)}
              disabled={isToggling}
              className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${
                enabled ? 'bg-green-500' : 'bg-gray-300'
              } ${isToggling ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                  enabled ? 'translate-x-8' : 'translate-x-1'
                }`}
              />
            </button>
          )}
        </div>

        {/* Badges */}
        <div className="flex flex-wrap gap-2 mt-3">
          {/* Pricing Badge */}
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${
              skill.pricing === 'free'
                ? 'bg-green-100 text-green-700'
                : skill.pricing === 'plus'
                ? 'bg-blue-100 text-blue-700'
                : skill.pricing === 'premium'
                ? 'bg-purple-100 text-purple-700'
                : 'bg-gray-100 text-gray-700'
            }`}
          >
            {skill.pricing === 'free' ? 'Kostenlos' : skill.pricing.toUpperCase()}
          </span>

          {/* API Key Badge */}
          {skill.requires_api_key && (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700 flex items-center gap-1">
              <Key className="w-3 h-3" />
              API-Key erforderlich
            </span>
          )}

          {/* Category Badge */}
          {skill.category && (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
              {skill.category}
            </span>
          )}
        </div>

        {/* Status */}
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center gap-2">
            {enabled ? (
              <>
                <Check className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-600">Aktiviert</span>
              </>
            ) : (
              <>
                <X className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-400">Deaktiviert</span>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderGroup = (title, skills, description) => {
    if (skills.length === 0) return null;

    return (
      <div className="mb-8">
        <div className="mb-4">
          <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
          <p className="text-gray-600 mt-1">{description}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {skills.map(renderSkillCard)}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Module werden geladen...</p>
        </div>
      </div>
    );
  }

  const selectedSenior = seniors.find(s => s.id === selectedSeniorId);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => window.history.back()}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Zurück</span>
          </button>

          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Module & Add-ons
          </h1>
          <p className="text-lg text-gray-600 mb-4">
            Verwalten Sie die verfügbaren Funktionen pro Senior
          </p>

          {/* Senior Selector */}
          {seniors.length > 0 && (
            <div className="bg-white rounded-xl shadow-md p-4 mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Senior auswählen:
              </label>
              <div className="flex gap-3 flex-wrap">
                {seniors.map(senior => (
                  <button
                    key={senior.id}
                    onClick={() => setSelectedSeniorId(senior.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                      selectedSeniorId === senior.id
                        ? 'bg-blue-600 text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <User className="w-4 h-4" />
                    {senior.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {selectedSenior && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                ℹ️ Einstellungen gelten nur für <strong>{selectedSenior.name}</strong>
              </p>
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-8">
          <div className="flex gap-3">
            <div className="text-blue-600 text-xl">ℹ️</div>
            <div>
              <h3 className="font-semibold text-blue-900 mb-1">
                Hinweis zu Modulen
              </h3>
              <p className="text-sm text-blue-800">
                <strong>Kern-Module</strong> (Erinnerungen, Notfall, Videoanrufe) sind immer
                aktiviert und können nicht deaktiviert werden. Andere Module können Sie je
                nach Bedarf ein- oder ausschalten.
              </p>
            </div>
          </div>
        </div>

        {/* Skill Groups */}
        {renderGroup(
          'Kern-Module',
          groupedSkills.core,
          'Grundlegende Funktionen, die immer verfügbar sind'
        )}

        {renderGroup(
          'Kostenlose Module',
          groupedSkills.free,
          'Zusätzliche Funktionen ohne Aufpreis'
        )}

        {renderGroup(
          'Plus-Module',
          groupedSkills.plus,
          'Erweiterte Funktionen für Plus-Abonnement'
        )}

        {renderGroup(
          'Premium-Module',
          groupedSkills.premium,
          'Premium-Funktionen mit KI-Unterstützung'
        )}

        {/* Empty State */}
        {!loading && seniorSkills.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">Keine Module verfügbar</p>
          </div>
        )}
      </div>
    </div>
  );
}
